import { useCallback, useState } from 'react';
import { useRecoilState } from 'recoil';
import { CONTENT_ELEMENT_ID } from '@/containers/AuthenticatedApp/constants';
import { DEFAULT_PAGINATION_CONFIG, PaginationField } from '@/components/pagination/constants';
import {
  isOfferedPageSize,
  pageSizeStore,
  UNPERSISTED_PAGINATION_KEY,
  type PaginationStorageKey,
} from '@/components/pagination/store';
import type { PaginationProps, Sorter } from '@/components/pagination';
import { isNil } from 'ramda';

export const DEFAULT_PAGINATION_STATE = {
  [PaginationField.Page]: DEFAULT_PAGINATION_CONFIG.defaultCurrent,
  [PaginationField.PageSize]: DEFAULT_PAGINATION_CONFIG.defaultPageSize,
} as const;

export interface Pagination<S extends string = string> {
  [PaginationField.Page]?: PaginationProps['current'];
  [PaginationField.PageSize]?: PaginationProps['pageSize'];
  [PaginationField.Sorts]?: Sorter<S>[];
  /**
   * @deprecated Use `sorts` instead.
   */
  [PaginationField.Sort]?: S;
}
export interface PaginationShape {
  pagination: Pagination;
  clear: () => void;
  changePagination: (newPage: Pagination['page'], newPageSize?: Pagination['pageSize']) => void;
  /**
   * @deprecated Use `changeSorts` instead.
   */
  changeSort: (newSort: Pagination['sort']) => void;
  changeSorts: (newSorts: Pagination['sorts']) => void;
  replace: (newFilter: Pagination) => void;
}

/**
 * Hook for managing the Pagination state.
 * @param initialPagination Initial state of pagination fields.
 * @param storageKey Key of the table under which its rows-per-page selection is remembered. Omit to not remember it.
 */
export const usePagination = (
  initialPagination: Partial<Pagination>,
  storageKey?: PaginationStorageKey,
): PaginationShape => {
  const [storedPageSize, setStoredPageSize] = useRecoilState(pageSizeStore(storageKey ?? UNPERSISTED_PAGINATION_KEY));

  const [pagination, setPagination] = useState<Pagination>(() => ({
    ...DEFAULT_PAGINATION_STATE,
    ...initialPagination,
    ...(isOfferedPageSize(storedPageSize) ? { [PaginationField.PageSize]: storedPageSize } : {}),
  }));

  const storePageSize = useCallback(
    (pageSize: Pagination['pageSize']) => {
      if (isNil(storageKey) || isNil(pageSize)) return;
      setStoredPageSize(pageSize);
    },
    [setStoredPageSize, storageKey],
  );

  const clear = useCallback(() => {
    setPagination(prevState => ({
      ...initialPagination,
      ...DEFAULT_PAGINATION_STATE,
      [PaginationField.PageSize]: prevState.pageSize,
    }));
  }, [initialPagination]);

  /** Complete replace of current pagination state. */
  const replace = useCallback(
    (newPagination: Pagination) => {
      storePageSize(newPagination.pageSize);
      setPagination(newPagination);
    },
    [storePageSize],
  );

  /** Updates the Pagination with update and sets the Page to 1. */
  const change = useCallback(
    (paginationUpdate: Pagination) => {
      storePageSize(paginationUpdate.pageSize);
      setPagination(prevState => ({
        ...prevState,
        [PaginationField.Page]: DEFAULT_PAGINATION_STATE.page,
        ...paginationUpdate,
      }));
    },
    [storePageSize],
  );

  /** Change the page and pageSize of the Pagination. */
  const changePagination = useCallback(
    (page: Pagination['page'], pageSize: Pagination['pageSize']) => {
      const content = document.getElementById(CONTENT_ELEMENT_ID);
      if (content) content.scrollTop = 0;
      change(isNil(pageSize) ? { page } : { page, pageSize });
    },
    [change],
  );

  /** Change the sort of the Pagination */
  const changeSort = useCallback(
    (newSort: Pagination['sort']) =>
      change({ [PaginationField.Sort]: newSort, [PaginationField.Page]: DEFAULT_PAGINATION_STATE.page }),
    [change],
  );

  const changeSorts = useCallback(
    (newSorts: Pagination['sorts']) =>
      change({ [PaginationField.Sorts]: newSorts, [PaginationField.Page]: DEFAULT_PAGINATION_STATE.page }),
    [change],
  );

  return {
    changePagination,
    changeSort,
    changeSorts,
    clear,
    pagination,
    replace,
  };
};
